import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, RefreshControl} from 'react-native';
import {Text, IconButton, Menu} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import WorkAssignmentStatusDialog from '../order/WorkAssignmentStatusDialog';

const DressAssignmentsScreen = () => {
  const {t} = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [menuVisibleId, setMenuVisibleId] = useState(null);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusAssignment, setStatusAssignment] = useState(null);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await workAssignmentApi.getAll();
      setAssignments(res.data?.data || []);
    } catch (error) {
      // silently fail on refresh
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchAssignments();
    }, [fetchAssignments]),
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAssignments();
    setRefreshing(false);
  };

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const getStatusColor = status => {
    switch (status) {
      case 'COMPLETED': return colors.success;
      case 'STARTED': return colors.primary;
      case 'DELETED': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const renderItem = ({item, index}) => (
    <View style={[styles.dataRow, index % 2 === 1 && styles.dataRowAlt]}>
      <View style={styles.nameCol}>
        <Text style={styles.nameText}>{item.workerName}</Text>
        {item.workerRole && <Text style={styles.roleText}>{item.workerRole}</Text>}
      </View>
      <View style={styles.itemCol}>
        <Text style={styles.cell}>{item.catalogItemName || '-'}</Text>
        <Text style={styles.codeText}>{item.instanceCode}</Text>
      </View>
      <Text style={[styles.cell, styles.rateCol]}>
        {item.pieceRate != null ? `\u20B9${Number(item.pieceRate)}` : '-'}
      </Text>
      <Text style={[styles.cell, styles.dateCol]}>{formatDate(item.assignedAt)}</Text>
      <Text style={[styles.cell, styles.statusCol, {color: getStatusColor(item.status)}]}>
        {item.status?.replace('_', ' ')}
      </Text>
      <View style={styles.moreCol}>
        <Menu
          visible={menuVisibleId === item.id}
          onDismiss={() => setMenuVisibleId(null)}
          anchor={
            <IconButton
              icon="dots-vertical"
              size={18}
              onPress={() => setMenuVisibleId(item.id)}
            />
          }>
          <Menu.Item
            title="Change Status"
            onPress={() => {
              setMenuVisibleId(null);
              setStatusAssignment(item);
              setStatusDialogVisible(true);
            }}
          />
        </Menu>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.tableHeader}>
        <Text style={[styles.headerCell, styles.nameCol]}>Worker</Text>
        <Text style={[styles.headerCell, styles.itemCol]}>Item</Text>
        <Text style={[styles.headerCell, styles.rateCol]}>Rate</Text>
        <Text style={[styles.headerCell, styles.dateCol]}>Date</Text>
        <Text style={[styles.headerCell, styles.statusCol]}>Status</Text>
        <Text style={[styles.headerCell, styles.moreCol]}>...</Text>
      </View>

      <FlatList
        data={assignments}
        keyExtractor={item => String(item.id)}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          <Text style={styles.emptyText}>{t('common.noData')}</Text>
        }
      />

      {statusAssignment && (
        <WorkAssignmentStatusDialog
          visible={statusDialogVisible}
          onDismiss={() => setStatusDialogVisible(false)}
          assignment={statusAssignment}
          onStatusChanged={fetchAssignments}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.surface},
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    alignItems: 'center',
  },
  headerCell: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.white,
    textAlign: 'center',
  },
  nameCol: {flex: 2},
  itemCol: {flex: 2},
  rateCol: {flex: 1.2, textAlign: 'center'},
  dateCol: {flex: 1.2, textAlign: 'center'},
  statusCol: {flex: 1.5, textAlign: 'center'},
  moreCol: {width: 36, alignItems: 'center'},
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  dataRowAlt: {backgroundColor: colors.background},
  cell: {fontSize: 12, color: colors.text, textAlign: 'center'},
  nameText: {fontSize: 12, fontWeight: '600', color: colors.text},
  roleText: {fontSize: 10, color: colors.textSecondary},
  codeText: {fontSize: 10, color: colors.textSecondary},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    fontSize: 16,
  },
});

export default DressAssignmentsScreen;
